import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SortIcon, TableModule } from 'primeng/table';
import { AlertaService } from '../../../../core/services/alerta.service';
import { IntranetSeguridadService } from '../../../../core/services/intranet-seguridad.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { StorageService } from '../../../../core/services/storage.service';

export type SeguridadKey = 'tipos-usuario' | 'permisos' | 'roles' | 'usuarios';
type Registro = any;

interface SeguridadConfig {
  titulo: string;
  singular: string;
  endpoint: string;
  idKey: string;
  columns: Array<{ key: string; label: string }>;
}

@Component({
  selector: 'app-tipos-usuario-page',
  templateUrl: './tipos-usuario.component.html',
  styleUrl: './tipos-usuario.component.scss',
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, TableModule, SortIcon],
})
export class TiposUsuarioComponent implements OnInit {
  @Input() seguridadKey: SeguridadKey = 'tipos-usuario';
  readonly configs: Record<SeguridadKey, SeguridadConfig> = {
    'tipos-usuario': {
      titulo: 'Tipos de usuario', singular: 'tipo de usuario', endpoint: 'tipos-usuario', idKey: 'IdTipoUsuario',
      columns: [{ key: 'Codigo', label: 'Código' }, { key: 'Descripcion', label: 'Descripción' }],
    },
    permisos: {
      titulo: 'Permisos', singular: 'permiso', endpoint: 'permisos', idKey: 'IdPermiso',
      columns: [{ key: 'Codigo', label: 'Código' }, { key: 'Descripcion', label: 'Descripción' }, { key: 'Ruta', label: 'Ruta' }, { key: 'Nivel', label: 'Nivel' }],
    },
    roles: {
      titulo: 'Roles', singular: 'rol', endpoint: 'roles', idKey: 'IdRol',
      columns: [{ key: 'Codigo', label: 'Código' }, { key: 'Nombre', label: 'Nombre' }, { key: 'Descripcion', label: 'Descripción' }],
    },
    usuarios: {
      titulo: 'Usuarios', singular: 'usuario', endpoint: 'usuarios', idKey: 'IdIntranetUsuario',
      columns: [{ key: 'Nombres', label: 'Nombres' }, { key: 'Usuario', label: 'Usuario' }, { key: 'TipoUsuario', label: 'Tipo de usuario' }, { key: 'Activo', label: 'Estado' }],
    },
  };

  config!: SeguridadConfig;
  key!: SeguridadKey;
  items: Registro[] = [];
  tiposUsuario: Registro[] = [];
  roles: Registro[] = [];
  permisos: Registro[] = [];
  registro: Registro = {};
  rolesSeleccionados: number[] = [];
  permisosUsuario: Record<number, number | null> = {};
  permisosRol: number[] = [];
  visibleDialog = false;
  guardando = false;

  constructor(
    private readonly seguridadService: IntranetSeguridadService,
    private readonly alertaService: AlertaService,
    private readonly loadingService: LoadingService,
    private readonly storageService: StorageService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.key = this.seguridadKey;
    this.config = this.configs[this.key];
    this.cargarDatos();
  }

  get tituloDialogo(): string {
    return this.registro[this.config.idKey] ? `Editar ${this.config.singular}` : `Nuevo ${this.config.singular}`;
  }

  get esUsuario(): boolean { return this.key === 'usuarios'; }
  get esRol(): boolean { return this.key === 'roles'; }
  get esPermiso(): boolean { return this.key === 'permisos'; }
  get esTipoUsuario(): boolean { return this.key === 'tipos-usuario'; }

  abrirNuevo(): void {
    this.inicializarFormulario();
    this.cargarDependencias();
    this.visibleDialog = true;
  }

  editar(item: Registro): void {
    this.registro = this.normalizarRegistro(item);
    this.rolesSeleccionados = [];
    this.permisosRol = [];
    this.permisosUsuario = {};
    this.cargarDependencias();

    if (this.esUsuario) {
      this.seguridadService.post<{ idsRol: number[]; permisos: Array<{ idPermiso: number; esPermitido: number }> }>('usuarios/configuracion', {
        idIntranetUsuario: item.IdIntranetUsuario,
      }).subscribe((response) => {
        if (response.isSuccess && response.value) {
          this.rolesSeleccionados = response.value.idsRol ?? [];
          this.permisosUsuario = Object.fromEntries((response.value.permisos ?? []).map((p) => [p.idPermiso, p.esPermitido]));
          this.cdr.detectChanges();
        }
      });
    }
    if (this.esRol) {
      this.seguridadService.post<number[]>('roles/permisos', { idRol: item.IdRol }).subscribe((response) => {
        if (response.isSuccess) {
          this.permisosRol = response.value ?? [];
          this.cdr.detectChanges();
        }
      });
    }
    this.visibleDialog = true;
  }

  guardar(): void {
    const error = this.validarFormulario();
    if (error) {
      this.alertaService.openAlerta('Alerta', error, 'warn');
      return;
    }
    this.guardando = true;
    this.loadingService.open('Guardando registro...');
    this.seguridadService.post<any>(`${this.config.endpoint}/guardar`, this.buildPayload()).subscribe({
      next: (response) => {
        if (!response.isSuccess) {
          this.finalizarGuardado();
          this.alertaService.openAlerta('Alerta', response.message, 'error');
          return;
        }
        const id = this.obtenerIdGuardado(response.value);
        if (!id) {
          this.finalizarGuardado();
          this.alertaService.openAlerta('Error', 'No se pudo identificar el registro guardado', 'error');
          return;
        }
        if (this.esUsuario) {
          this.guardarConfiguracionUsuario(id, response.message);
          return;
        }
        if (this.esRol) {
          this.guardarPermisosRol(id, response.message);
          return;
        }
        this.completarGuardado(response.message);
      },
      error: (error) => this.mostrarError(error),
    });
  }

  confirmarBaja(item: Registro): void {
    const activo = Number(item.Activo ?? 1) === 1;
    const mensaje = activo ? `¿Deseas dar de baja a ${this.nombreItem(item)}?` : `¿Deseas reactivar a ${this.nombreItem(item)}?`;
    this.alertaService.openAlertaDesicion(activo ? 'Dar de baja' : 'Reactivar usuario', mensaje, activo ? 'warn' : 'info', () => {
      this.registro = { ...item, activo: activo ? 0 : 1 };
      this.loadingService.open(activo ? 'Dando de baja...' : 'Reactivando usuario...');
      this.seguridadService.post<any>('usuarios/guardar', this.buildPayload()).subscribe({
        next: (response) => response.isSuccess ? this.completarGuardado(response.message) : this.mostrarRespuestaError(response.message),
        error: (error) => this.mostrarError(error),
      });
    });
  }

  confirmarEliminar(item: Registro): void {
    this.alertaService.openAlertaDesicion('Eliminar registro', `¿Deseas eliminar ${this.nombreItem(item)}?`, 'warn', () => {
      this.loadingService.open('Eliminando registro...');
      this.seguridadService.post<any>(`${this.config.endpoint}/eliminar`, { [this.config.idKey.charAt(0).toLowerCase() + this.config.idKey.slice(1)]: item[this.config.idKey] }).subscribe({
        next: (response) => response.isSuccess ? this.completarGuardado(response.message) : this.mostrarRespuestaError(response.message),
        error: (error) => this.mostrarError(error),
      });
    });
  }

  cambiarRol(id: number, checked: boolean): void {
    this.rolesSeleccionados = checked ? [...this.rolesSeleccionados, id] : this.rolesSeleccionados.filter((value) => value !== id);
  }

  cambiarPermisoRol(id: number, checked: boolean): void {
    const afectados = this.obtenerPermisosDelModulo(id);
    this.permisosRol = checked
      ? [...new Set([...this.permisosRol, ...afectados])]
      : this.permisosRol.filter((value) => !afectados.includes(value));
  }

  estadoUsuario(item: Registro): string { return this.esActivo(item) ? 'Activo' : 'Inactivo'; }
  esActivo(item: Registro): boolean { return Number(item.Activo ?? item.activo ?? 1) === 1; }
  esModulo(permiso: Registro): boolean { return permiso.IdPermisoPadre === null || permiso.IdPermisoPadre === undefined || Number(permiso.IdPermisoPadre) === 0; }
  nombreItem(item: Registro): string { return item.Nombres ?? item.Nombre ?? item.Descripcion ?? item.Codigo ?? 'este registro'; }

  permisosRaiz(): Registro[] {
    return this.permisos.filter((permiso) => this.esModulo(permiso));
  }

  permisosHijos(idPermiso: number): Registro[] {
    const encontrados = this.obtenerPermisosDelModulo(idPermiso);
    return this.permisos.filter((permiso) => Number(permiso.IdPermiso) !== Number(idPermiso) && encontrados.includes(Number(permiso.IdPermiso)));
  }

  private cargarDatos(): void {
    this.loadingService.open('Cargando registros...');
    this.seguridadService.post<Registro[]>(`${this.config.endpoint}/listar`).subscribe({
      next: (response) => {
        this.loadingService.close();
        this.items = response.isSuccess ? response.value ?? [] : [];
        if (!response.isSuccess) this.alertaService.openAlerta('Alerta', response.message, 'error');
        this.cdr.detectChanges();
      },
      error: (error) => this.mostrarError(error),
    });
  }

  private cargarDependencias(): void {
    if (this.esUsuario) {
      this.seguridadService.post<Registro[]>('tipos-usuario/listar').subscribe((r) => { if (r.isSuccess) this.tiposUsuario = r.value ?? []; });
      this.seguridadService.post<Registro[]>('roles/listar').subscribe((r) => { if (r.isSuccess) this.roles = r.value ?? []; });
    }
    if (this.esUsuario || this.esRol || this.esPermiso) {
      this.seguridadService.post<Registro[]>('permisos/listar').subscribe((r) => { if (r.isSuccess) this.permisos = r.value ?? []; });
    }
  }

  private inicializarFormulario(): void {
    this.registro = this.esUsuario ? { nombres: '', usuario: '', clave: '', idTipoUsuario: null, activo: 1 } :
      this.esRol ? { nombre: '', codigo: '', descripcion: '' } :
      this.esPermiso ? { codigo: '', descripcion: '', ruta: '', icono: '', nivel: 1, orden: 0, idPermisoPadre: null, esMenu: 1 } :
      { codigo: '', descripcion: '' };
    this.rolesSeleccionados = [];
    this.permisosRol = [];
    this.permisosUsuario = {};
  }

  private normalizarRegistro(item: Registro): Registro {
    if (this.esUsuario) return {
      ...item,
      nombres: item.Nombres,
      usuario: item.Usuario,
      idTipoUsuario: item.IdTipoUsuario,
      activo: item.Activo,
      clave: '',
    };
    if (this.esRol) return { ...item, nombre: item.Nombre, codigo: item.Codigo, descripcion: item.Descripcion };
    if (this.esPermiso) return {
      ...item,
      codigo: item.Codigo,
      descripcion: item.Descripcion,
      ruta: item.Ruta,
      icono: item.Icono,
      nivel: item.Nivel,
      orden: item.Orden,
      idPermisoPadre: item.IdPermisoPadre,
      esMenu: item.EsMenu,
    };
    return { ...item, codigo: item.Codigo, descripcion: item.Descripcion };
  }

  private obtenerPermisosDelModulo(idPermiso: number): number[] {
    const encontrados = new Set<number>([Number(idPermiso)]);
    let cambio = true;

    while (cambio) {
      cambio = false;
      for (const permiso of this.permisos) {
        const id = Number(permiso.IdPermiso);
        const padre = Number(permiso.IdPermisoPadre ?? 0);
        if (encontrados.has(padre) && !encontrados.has(id)) {
          encontrados.add(id);
          cambio = true;
        }
      }
    }
    return [...encontrados];
  }

  private validarFormulario(): string | null {
    if (this.esUsuario) {
      if (!this.registro.nombres?.trim() || !this.registro.usuario?.trim() || !this.registro.idTipoUsuario) return 'Nombres, usuario y tipo de usuario son obligatorios';
      if (!this.registro.IdIntranetUsuario && !this.registro.clave?.trim()) return 'La clave es obligatoria al crear un usuario';
    } else if (this.esRol && (!this.registro.nombre?.trim() || !this.registro.codigo?.trim())) return 'Nombre y código son obligatorios';
    else if (this.esRol && this.permisosRol.length === 0) return 'Selecciona al menos un permiso para el rol';
    else if (this.esPermiso && (!this.registro.codigo?.trim() || !this.registro.descripcion?.trim())) return 'Código y descripción son obligatorios';
    else if (this.esTipoUsuario && (!this.registro.codigo?.trim() || !this.registro.descripcion?.trim())) return 'Código y descripción son obligatorios';
    return null;
  }

  private buildPayload(): Registro {
    const usuario = this.storageService.storageGet<any>('usuario');
    if (this.esUsuario) return {
      idIntranetUsuario: this.registro.IdIntranetUsuario,
      nombres: this.registro.nombres ?? this.registro.Nombres,
      usuario: this.registro.usuario ?? this.registro.Usuario,
      clave: this.registro.clave,
      idTipoUsuario: Number(this.registro.idTipoUsuario ?? this.registro.IdTipoUsuario),
      activo: Number(this.registro.activo ?? this.registro.Activo ?? 1),
      usuarioCreacion: usuario?.idIntranetUsuario ?? null,
    };
    if (this.esRol) return { idRol: this.registro.IdRol, nombre: this.registro.nombre ?? this.registro.Nombre, codigo: this.registro.codigo ?? this.registro.Codigo, descripcion: this.registro.descripcion ?? this.registro.Descripcion, usuarioCreacion: usuario?.idIntranetUsuario ?? null };
    if (this.esPermiso) return { idPermiso: this.registro.IdPermiso, codigo: this.registro.codigo ?? this.registro.Codigo, descripcion: this.registro.descripcion ?? this.registro.Descripcion, ruta: this.registro.ruta ?? this.registro.Ruta, icono: this.registro.icono ?? this.registro.Icono, nivel: Number(this.registro.nivel ?? this.registro.Nivel ?? 1), orden: Number(this.registro.orden ?? this.registro.Orden ?? 0), idPermisoPadre: this.registro.idPermisoPadre ?? this.registro.IdPermisoPadre ?? null, esMenu: Number(this.registro.esMenu ?? this.registro.EsMenu ?? 1) };
    return { idTipoUsuario: this.registro.IdTipoUsuario, codigo: this.registro.codigo ?? this.registro.Codigo, descripcion: this.registro.descripcion ?? this.registro.Descripcion };
  }

  private obtenerIdGuardado(data: Registro | null): number {
    const idRespuesta = this.esUsuario
      ? data?.idIntranetUsuario
      : this.esRol
        ? data?.idRol
        : this.esPermiso
          ? data?.idPermiso
          : data?.idTipoUsuario;
    return Number(idRespuesta ?? this.registro[this.config.idKey] ?? 0);
  }

  private guardarConfiguracionUsuario(id: number, mensaje: string): void {
    const usuario = this.storageService.storageGet<any>('usuario');
    this.seguridadService.post<any>('usuarios/asignar-roles', { idIntranetUsuario: id, idsRol: this.rolesSeleccionados, usuarioCreacion: usuario?.idIntranetUsuario ?? null }).subscribe({
      next: (roles) => {
        if (!roles.isSuccess) return this.mostrarRespuestaError(roles.message);
        const permisos = Object.entries(this.permisosUsuario).filter(([, valor]) => valor !== null).map(([idPermiso, esPermitido]) => ({ idPermiso: Number(idPermiso), esPermitido: Number(esPermitido) }));
        this.seguridadService.post<any>('usuarios/asignar-permisos', { idIntranetUsuario: id, permisos, usuarioCreacion: usuario?.idIntranetUsuario ?? null }).subscribe({
          next: (respuesta) => respuesta.isSuccess ? this.completarGuardado(mensaje) : this.mostrarRespuestaError(respuesta.message),
          error: (error) => this.mostrarError(error),
        });
      }, error: (error) => this.mostrarError(error),
    });
  }

  private guardarPermisosRol(id: number, mensaje: string): void {
    const usuario = this.storageService.storageGet<any>('usuario');
    this.seguridadService.post<any>('roles/asignar-permisos', { idRol: id, idsPermiso: this.permisosRol, usuarioCreacion: usuario?.idIntranetUsuario ?? null }).subscribe({
      next: (response) => response.isSuccess ? this.completarGuardado(mensaje) : this.mostrarRespuestaError(response.message),
      error: (error) => this.mostrarError(error),
    });
  }

  private completarGuardado(mensaje: string): void {
    this.loadingService.close();
    this.guardando = false;
    this.visibleDialog = false;
    this.alertaService.openAlerta('Correcto', mensaje, 'success');
    this.cargarDatos();
  }

  private finalizarGuardado(): void { this.loadingService.close(); this.guardando = false; }
  private mostrarRespuestaError(mensaje: string): void { this.finalizarGuardado(); this.alertaService.openAlerta('Alerta', mensaje, 'error'); }
  private mostrarError(error: any): void { this.finalizarGuardado(); this.alertaService.openAlerta('Error', error?.message ?? 'No se pudo completar la operación', 'error'); }
}
